/**
 * Field formatters and display labels for the Employees change-history feed.
 *
 * Used by: `EmployeeDetailPanel.tsx` -> `<ChangeHistory />`.
 */

import {
  DIRECTOR_RELATIONSHIP_LABELS,
  EMPLOYMENT_BASIS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  RESIDENCY_LABELS,
  TERMINATION_TYPE_LABELS,
} from "@cvx/lib/employeeEntitlements";

const EM_DASH = "\u2014";

const fmtCurrency = (v: number): string =>
  v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  });

const fmtDate = (raw: string): string => {
  try {
    return new Date(`${raw}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const money = (v: unknown): string =>
  typeof v === "number" ? fmtCurrency(v) : String(v ?? EM_DASH);

const date = (v: unknown): string =>
  typeof v === "string" && v ? fmtDate(v) : EM_DASH;

const enumLabel =
  (map: Record<string, string>) =>
  (v: unknown): string =>
    map[String(v)] ?? String(v ?? EM_DASH);

/**
 * Renders a whole FEG input object as a compact `key: value` list — the
 * `updateFegDetail` mutation replaces the section wholesale, so the
 * history entry carries the object rather than a single scalar.
 */
const detailObject =
  (labels: Record<string, string>, currencyKeys: Set<string>) =>
  (v: unknown): string => {
    if (v == null || typeof v !== "object") return EM_DASH;
    const parts: string[] = [];
    for (const [key, raw] of Object.entries(v as Record<string, unknown>)) {
      if (raw == null || raw === "") continue;
      const label = labels[key] ?? key;
      const rendered =
        typeof raw === "number" && currencyKeys.has(key)
          ? fmtCurrency(raw)
          : String(raw);
      parts.push(`${label}: ${rendered}`);
    }
    return parts.length > 0 ? parts.join(", ") : EM_DASH;
  };

const WAGES_DETAIL_LABELS: Record<string, string> = {
  totalOutstanding: "Total outstanding",
  unpaidInPeriod: "Unpaid in period",
  allowancesInPeriod: "Allowances in period",
  commissionInPeriod: "Commission in period",
  otherEntitlements: "Other entitlements",
  comments: "Comments",
};

const LEAVE_DETAIL_LABELS: Record<string, string> = {
  hoursAtTermination: "Hours at termination",
  windUpCost: "Winding-up cost",
  comments: "Comments",
};

const LEAVE_LOADING_DETAIL_LABELS: Record<string, string> = {
  ratePercent: "Rate (%)",
  windUpCost: "Winding-up cost",
  comments: "Comments",
};

const WEEKS_DETAIL_LABELS: Record<string, string> = {
  weeksOwing: "Weeks owing",
  windUpCost: "Winding-up cost",
  comments: "Comments",
};

const PAT_LABELS: Record<string, string> = {
  unpaidWages: "Unpaid wages paid",
  annualLeave: "Annual leave paid",
  longServiceLeave: "Long service leave paid",
  piln: "Notice paid",
  redundancy: "Redundancy paid",
  comments: "Comments",
};

export const EMPLOYEE_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  // --- Identity ---
  name: (v) => String(v ?? EM_DASH),
  familyName: (v) => String(v ?? EM_DASH),
  firstName: (v) => String(v ?? EM_DASH),
  dateOfBirth: date,
  residencyStatus: enumLabel(RESIDENCY_LABELS),
  relationshipToDirector: enumLabel(DIRECTOR_RELATIONSHIP_LABELS),
  isRelatedParty: (v) => (v ? "Yes" : "No"),

  // --- Employment ---
  role: (v) => String(v ?? EM_DASH),
  employmentCategory: enumLabel(EMPLOYMENT_BASIS_LABELS),
  employmentType: enumLabel(EMPLOYMENT_TYPE_LABELS),
  stateOfEmployment: (v) => String(v ?? EM_DASH),
  startDate: date,
  averageWeeklyHours: (v) =>
    typeof v === "number" ? `${v} hrs/week` : String(v ?? EM_DASH),
  wageRate: (v) =>
    typeof v === "number" ? `${fmtCurrency(v)}/hr` : String(v ?? EM_DASH),
  regularWeeklyAllowances: money,
  awardClassification: (v) => String(v ?? EM_DASH),

  // --- End of employment ---
  terminationDate: date,
  terminationType: enumLabel(TERMINATION_TYPE_LABELS),
  notificationDate: date,

  // --- Entitlement amounts ---
  unpaidWages: money,
  unpaidSuper: money,
  accruedAnnualLeave: money,
  leaveLoading: money,
  accruedLongServiceLeave: money,
  paymentInLieuOfNotice: money,
  redundancyPay: money,

  // --- FEG input objects ---
  wagesDetail: detailObject(
    WAGES_DETAIL_LABELS,
    new Set([
      "totalOutstanding",
      "unpaidInPeriod",
      "allowancesInPeriod",
      "commissionInPeriod",
      "otherEntitlements",
    ]),
  ),
  annualLeaveDetail: detailObject(LEAVE_DETAIL_LABELS, new Set(["windUpCost"])),
  leaveLoadingDetail: detailObject(
    LEAVE_LOADING_DETAIL_LABELS,
    new Set(["windUpCost"]),
  ),
  longServiceLeaveDetail: detailObject(
    LEAVE_DETAIL_LABELS,
    new Set(["windUpCost"]),
  ),
  pilnDetail: detailObject(WEEKS_DETAIL_LABELS, new Set(["windUpCost"])),
  redundancyDetail: detailObject(WEEKS_DETAIL_LABELS, new Set(["windUpCost"])),
  paymentsAfterTermination: detailObject(
    PAT_LABELS,
    new Set([
      "unpaidWages",
      "annualLeave",
      "longServiceLeave",
      "piln",
      "redundancy",
    ]),
  ),

  notes: (v) => String(v ?? EM_DASH),
};

export const EMPLOYEE_FIELD_LABELS: Record<string, string> = {
  name: "Name",
  familyName: "Family name",
  firstName: "First name",
  role: "Role",
  employmentCategory: "Basis of employment",
  employmentType: "Employment type",
  stateOfEmployment: "State of employment",
  startDate: "Start date",
  terminationDate: "Termination date",
  terminationType: "Termination type",
  notificationDate: "Notification date",
  averageWeeklyHours: "Avg weekly hours",
  wageRate: "Hourly rate",
  regularWeeklyAllowances: "Regular weekly allowances",
  awardClassification: "Award classification",
  dateOfBirth: "Date of birth",
  residencyStatus: "Residency",
  relationshipToDirector: "Relationship to director",
  isRelatedParty: "Related party",
  notes: "Notes",
  unpaidWages: "Unpaid wages",
  unpaidSuper: "Superannuation",
  accruedAnnualLeave: "Annual leave",
  leaveLoading: "Leave loading",
  accruedLongServiceLeave: "Long-service leave",
  paymentInLieuOfNotice: "PILON",
  redundancyPay: "Redundancy",
  wagesDetail: "Unpaid wages breakdown",
  annualLeaveDetail: "Annual leave inputs",
  leaveLoadingDetail: "Leave loading inputs",
  longServiceLeaveDetail: "Long-service leave inputs",
  pilnDetail: "Notice inputs",
  redundancyDetail: "Redundancy inputs",
  paymentsAfterTermination: "Payments after termination",
};
